(function () {
  'use strict';

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function getGapPx(track) {
    let style = window.getComputedStyle(track);
    let g = style.columnGap || style.gap || '0';
    let n = parseFloat(g, 10);
    return isNaN(n) ? 0 : n;
  }

  function scrollStep(viewport, dir) {
    let track = qs(viewport, '[data-ugc-track]');
    if (!track) return;
    let slide = track.querySelector('.ugc-video-carousel__slide');
    if (!slide) return;
    let amount = slide.offsetWidth + getGapPx(track);
    let rtl = getComputedStyle(viewport).direction === 'rtl';
    let delta = dir * amount * (rtl ? -1 : 1);
    viewport.scrollBy({ left: delta, behavior: 'smooth' });
  }

  function updateNavState(viewport, prevBtn, nextBtn, layout) {
    let max = viewport.scrollWidth - viewport.clientWidth;
    let tol = 2;
    if (prevBtn) prevBtn.disabled = viewport.scrollLeft <= tol;
    if (nextBtn) nextBtn.disabled = viewport.scrollLeft >= max - tol;
    if (layout && prevBtn && nextBtn) {
      let hideNav = prevBtn.disabled && nextBtn.disabled;
      layout.classList.toggle('ugc-video-carousel__layout--nav-hidden', hideNav);
    }
  }

  function loadVideoSource(video, preloadMode) {
    if (!video.dataset.src) return false;
    video.preload = preloadMode || 'metadata';
    video.src = video.dataset.src;
    video.removeAttribute('data-src');
    try {
      video.load();
    } catch (e) {}
    return true;
  }

  function ensureVideoSrcLoaded(video) {
    if (video.src && String(video.src).length > 0) {
      if (video.readyState >= 2) {
        return Promise.resolve();
      }
      return new Promise(function (resolve, reject) {
        function done() {
          cleanup();
          resolve();
        }
        function cleanup() {
          video.removeEventListener('canplay', done);
          video.removeEventListener('loadeddata', done);
          video.removeEventListener('error', onErr);
        }
        function onErr() {
          cleanup();
          reject(new Error('Video load error'));
        }
        video.addEventListener('canplay', done, { once: true });
        video.addEventListener('loadeddata', done, { once: true });
        video.addEventListener('error', onErr, { once: true });
      });
    }
    let pending = video.dataset.src;
    if (!pending) {
      return Promise.reject(new Error('No video source'));
    }
    return new Promise(function (resolve, reject) {
      function cleanup() {
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('canplay', onLoaded);
        video.removeEventListener('error', onErr);
      }
      function onLoaded() {
        cleanup();
        resolve();
      }
      function onErr() {
        cleanup();
        reject(new Error('Video load error'));
      }
      video.addEventListener('loadeddata', onLoaded, { once: true });
      video.addEventListener('canplay', onLoaded, { once: true });
      video.addEventListener('error', onErr, { once: true });
      video.preload = 'metadata';
      video.src = pending;
      video.removeAttribute('data-src');
      try {
        video.load();
      } catch (e) {
        cleanup();
        reject(e);
      }
    });
  }

  function allowsControls(video, opts) {
    if (opts.showControls === false) return false;
    return video.dataset.videoControls !== 'false';
  }

  function showNativeControls(video, opts) {
    if (!allowsControls(video, opts)) return;
    video.controls = true;
    video.setAttribute('controls', '');
  }

  function hideNativeControls(video, opts) {
    if (!allowsControls(video, opts)) return;
    video.controls = false;
    video.removeAttribute('controls');
  }

  function stopAndResetVideo(video, slide, opts) {
    video.pause();
    try {
      video.currentTime = 0;
    } catch (e) {}
    hideNativeControls(video, opts);
    slide.classList.toggle('ugc-video-carousel__slide--playing', false);
  }

  function attachVideo(slide, video, opts) {
    let media = qs(slide, '.ugc-video-carousel__media');
    let hoverTarget = media || slide;
    let playBtn = qs(slide, '[data-ugc-play]');
    let autoPlayWhenVisible = video.dataset.videoAutoPlay === 'true';

    function markPlaying(on) {
      slide.classList.toggle('ugc-video-carousel__slide--playing', on);
    }

    function playVideo() {
      function startPlayback() {
        let startMuted = video.dataset.videoMuted !== 'false';
        video.muted = startMuted;
        let p = video.play();
        if (p && typeof p.then === 'function') {
          p.catch(function () {});
        }
      }

      if (!video.src && video.dataset.src) {
        ensureVideoSrcLoaded(video).then(startPlayback).catch(function () {});
      } else {
        startPlayback();
      }
    }

    hoverTarget.addEventListener(
      'mouseenter',
      function () {
        showNativeControls(video, opts);
        let allowAutoplay = video.dataset.videoAutoplay !== 'false';
        if (opts.hoverAutoplay && allowAutoplay) {
          playVideo();
          markPlaying(true);
        }
      },
      { passive: true }
    );

    if (playBtn) {
      playBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        showNativeControls(video, opts);
        playVideo();
        markPlaying(true);
      });
    }

    video.addEventListener('play', function () {
      markPlaying(true);
    });

    video.addEventListener('pause', function () {
      if (video.currentTime === 0 || video.ended) markPlaying(false);
    });

    hoverTarget.addEventListener(
      'mouseleave',
      function () {
        if (autoPlayWhenVisible) return;
        stopAndResetVideo(video, slide, opts);
      },
      { passive: true }
    );

    let io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && autoPlayWhenVisible) {
            playVideo();
            markPlaying(true);
            return;
          }
          if (!entry.isIntersecting) {
            stopAndResetVideo(video, slide, opts);
          }
        });
      },
      { root: null, threshold: 0.55 }
    );
    io.observe(slide);

    return { playVideo: playVideo };
  }

  function lazyLoadVideo(video) {
    if (!video.dataset.src) return;

    let observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          if (!video.dataset.src) return;
          loadVideoSource(video, 'metadata');
        });
      },
      {
        root: null,
        rootMargin: '420px 0px 420px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(video);
  }

  let FIRST_CLIP_AUTO_COUNT = 4;

  function scheduleSmartLoads(root, videos) {
    let allLoaded = false;

    function loadAllClips() {
      if (allLoaded) return;
      allLoaded = true;
      videos.forEach(function (video, index) {
        if (!video.dataset.src) return;
        let mode = index < FIRST_CLIP_AUTO_COUNT ? 'auto' : 'metadata';
        loadVideoSource(video, mode);
      });
    }

    let sectionAnchor =
      root.closest('.shopify-section') ||
      root.closest('section') ||
      root.parentElement;

    let secIo = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            loadAllClips();
            secIo.disconnect();
          }
        });
      },
      {
        root: null,
        rootMargin: '700px 0px 700px 0px',
        threshold: 0,
      }
    );

    secIo.observe(sectionAnchor || root);

    requestAnimationFrame(function () {
      try {
        let el = sectionAnchor || root;
        let r = el.getBoundingClientRect();
        let vh = window.innerHeight || document.documentElement.clientHeight;
        if (r.bottom > -300 && r.top < vh + 900) {
          loadAllClips();
          secIo.disconnect();
        }
      } catch (e) {}
    });
  }

  function initCarousel(root) {
    if (root.dataset.ugcInitialized === 'true') return;
    root.dataset.ugcInitialized = 'true';

    let viewport = qs(root, '[data-ugc-viewport]');
    let layout = qs(root, '.ugc-video-carousel__layout');
    let prevBtn = qs(root, '[data-ugc-prev]');
    let nextBtn = qs(root, '[data-ugc-next]');
    let hoverAutoplay = root.getAttribute('data-hover-autoplay') === 'true';
    let controlsAttr = root.getAttribute('data-show-controls');
    let showControls = controlsAttr === null ? true : controlsAttr === 'true';

    if (!viewport) return;

    let slides = qsa(root, '.ugc-video-carousel__slide');
    let videos = slides
      .map(function (slide) {
        return qs(slide, '.ugc-video-carousel__video');
      })
      .filter(Boolean);

    videos.slice(0, FIRST_CLIP_AUTO_COUNT).forEach(function (video, index) {
      if (!video.dataset.src) return;
      let mode = index === 0 ? 'auto' : 'metadata';
      loadVideoSource(video, mode);
    });

    scheduleSmartLoads(root, videos);

    slides.forEach(function (slide) {
      let video = qs(slide, '.ugc-video-carousel__video');
      if (!video) return;
      video.loop = video.dataset.videoLoop !== 'false';
      attachVideo(slide, video, {
        hoverAutoplay: hoverAutoplay,
        showControls: showControls,
      });
    });

    function onScroll() {
      updateNavState(viewport, prevBtn, nextBtn, layout);
    }

    viewport.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollStep(viewport, -1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollStep(viewport, 1);
      });
    }

    onScroll();
  }

  function initAll() {
    qsa(document, '[data-ugc-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    let section = event.target;
    if (!section || !section.querySelector) return;
    let root = section.querySelector('[data-ugc-carousel]');
    if (root) {
      root.dataset.ugcInitialized = 'false';
      initCarousel(root);
    }
  });
})();
